/**
 * Initials and a stable colour for an account.
 *
 * ── The colour is DERIVED, never stored ──────────────────────────────
 *
 * The portal this replaces kept a hex value on rows in the database — `iconBg`
 * on every application, `dot` on every activity entry — which made a rebrand a
 * migration, and made two views of the same account disagree whenever one of
 * them forgot to read the column.
 *
 * Hashing the identifier gives the same colour for the same account on every
 * screen, on every machine, forever, with nothing to store and nothing to keep
 * in step. Which colour any given account gets does not matter; that it is the
 * same one each time is the entire feature.
 */

/** From `@ridgewill/brand`'s palette, chosen to stay legible with white text. */
const PALETTE = ['#2B63E8', '#0E9D6B', '#7C5CFC', '#D9890B', '#2B8FB5', '#C4476B'] as const;

export function avatarColor(id: string): string {
  let h = 0;
  // `>>> 0` after each step keeps this in unsigned 32-bit range. Without it the
  // value goes negative and the modulo below can return a negative index — an
  // undefined lookup, which under `noUncheckedIndexedAccess` is a type error
  // and at runtime would be a blank avatar.
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length] ?? PALETTE[0];
}

/**
 * Up to two letters, from a name or an email address.
 *
 * Falls back to the local part of an email, because most accounts here have no
 * name set and `@` is not an initial. An empty result renders as a blank circle
 * rather than as a wrong letter — better to say nothing than to label somebody
 * with a character from their domain.
 */
export function initials(nameOrEmail: string): string {
  const raw = nameOrEmail.trim();
  if (!raw) return '';

  const source = raw.includes('@') && !raw.includes(' ') ? (raw.split('@')[0] ?? '') : raw;
  const parts = source.split(/[\s._-]+/).filter(Boolean);

  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase();
}
